'use client';

import { useState, useEffect } from 'react';
import { getPortName } from '@/lib/carriers/un-locode';
import { SaveShipmentButton } from './save-shipment-button';
import { DestinationCTA } from './destination-cta';
import {
  decodeISOEquipment,
  eventLabel,
  extractContainersFromEvents,
  groupContainersByType,
} from '@/lib/tracking-utils';
import { TrackingEmptyState } from './tracking-empty-state';
import { recordProductEvent } from '@/lib/product-analytics';

interface DocumentReference {
  documentReferenceType: string;
  documentReferenceValue: string;
}

interface SealInfo {
  sealNumber: string;
  sealSource?: string;
  sealType?: string;
}

interface TrackingEvent {
  carrier: string;
  eventType: string;
  eventClassifierCode: 'ACT' | 'EST' | 'PLN';
  eventDateTime: string;
  transportEventTypeCode?: string;
  equipmentEventTypeCode?: string;
  shipmentEventTypeCode?: string;
  modeOfTransport?: string;
  vesselName?: string;
  voyageNumber?: string;
  UNLocationCode?: string;
  locationName?: string;
  facilityTypeCode?: string;
  equipmentReference?: string;
  ISOEquipmentCode?: string;
  emptyIndicatorCode?: string;
  description?: string;
  documentReferences?: DocumentReference[];
  seals?: SealInfo[];
  vesselIMONumber?: string;
  vesselFlag?: string;
  vesselCallSign?: string;
  facilityCode?: string;
  carrierServiceCode?: string;
  facilityAddress?: string;
  [key: string]: unknown;
}

interface TrackingResult {
  reference: string;
  referenceType: string;
  detectedCarrier: string | null;
  /** Compact paywall responses identify the carrier without dispatch metadata. */
  carrier?: string;
  suggestedCarrier: string | null;
  suggestedCarrierStage: string | null;
  carriersQueried: string[];
  events: TrackingEvent[];
  errors: string[];
  carrierErrors: string[];
  /** Set on premium-tier paywall responses (WIDGET-01). */
  paywall?: boolean;
  unavailable?: boolean;
  /** Paywall CTA copy when paywall=true. */
  message?: string;
  /** Paywall upgrade URL when paywall=true. */
  upgradeUrl?: string;
  upgradeLabel?: string;
  /**
   * True when `events[]` is served from the local archive (paywall path or
   * fallback when every carrier was disabled / errored). Used to badge the
   * tracking card so users know the data may be stale.
   */
  cached?: boolean;
  /**
   * True when every targeted carrier returned empty AND we fell back to the
   * archive. Set by the kill-switch / empty-result fallback in /api/track.
   */
  stale?: boolean;
}

interface RouteStop {
  key: string;
  name: string;
  code?: string;
  status: 'past' | 'current' | 'future';
  vessel?: string;
  voyage?: string;
  departedAt?: string;
  arrivedAt?: string;
}

// Terminal/facility name indicators — these are NOT city-level port names
const TERMINAL_RE = /\b(TERMINAL|DEPOT|YARD|CONTAINER|FREIGHT|GATE|BERTH|CFS|ICD|LOGISTICS|WAREHOUSE|FACILITY|INLAND|DEPO)\b|\(/i;

function isTerminalName(name: string): boolean {
  return TERMINAL_RE.test(name);
}

// Build a map of UNLocationCode → best (city-level) locationName.
// Collects all names seen for each code and prefers ones that don't look like
// terminal/facility names (e.g. "HAMBURG" over "EUROGATE TERMINAL HAMBURG").
function buildLocationMap(events: TrackingEvent[]): Record<string, string> {
  const allNames: Record<string, string[]> = {};
  for (const e of events) {
    if (e.UNLocationCode && e.locationName) {
      (allNames[e.UNLocationCode] ??= []).push(e.locationName);
    }
  }
  const map: Record<string, string> = {};
  for (const [code, names] of Object.entries(allNames)) {
    const clean = names.filter((n) => !isTerminalName(n));
    map[code] = clean[0] ?? names[0];
  }
  return map;
}

// Format a location as "City Name, CC" where CC is the ISO country code
// extracted from the first 2 chars of the UN location code.
//
// Name resolution priority:
//   1. UN/LOCODE lookup table (always city-level)
//   2. Raw locationName if it's not a terminal/facility name
//   3. buildLocationMap best name (across all events for this code)
//   4. Raw locationName as last resort
function formatLocation(
  locationName: string | undefined | null,
  unLocationCode: string | undefined | null,
  locationMap?: Record<string, string>
): string | null {
  const country = unLocationCode?.slice(0, 2) ?? null;

  // Priority 1: LOCODE lookup — always a clean city name
  const locodeName = unLocationCode ? getPortName(unLocationCode) : null;
  if (locodeName) {
    return country ? `${locodeName}, ${country}` : locodeName;
  }

  // Priority 2: raw name if it looks city-level
  const mapName = unLocationCode ? locationMap?.[unLocationCode] : undefined;
  const name = (locationName && !isTerminalName(locationName))
    ? locationName
    : mapName ?? locationName;

  if (!name && !unLocationCode) return null;

  if (name) {
    // Title-case the name (MSC/ZIM return it all-caps)
    const titleName = name
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
    return country ? `${titleName}, ${country}` : titleName;
  }

  return unLocationCode ?? null;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatShort(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

const DOC_REF_LABELS: Record<string, string> = {
  BKG: 'Booking',
  TRD: 'Bill of Lading',
  SHI: 'Shipping Instruction',
};


function hasEventDetails(event: TrackingEvent): boolean {
  return !!(
    event.equipmentReference ||
    event.ISOEquipmentCode ||
    event.emptyIndicatorCode ||
    event.description ||
    (event.documentReferences && event.documentReferences.length > 0) ||
    (event.seals && event.seals.length > 0) ||
    event.vesselIMONumber ||
    event.vesselFlag ||
    event.vesselCallSign ||
    event.facilityCode ||
    event.carrierServiceCode ||
    event.facilityAddress
  );
}

// Extract ordered route stops from transport events
function extractRoute(events: TrackingEvent[], locationMap?: Record<string, string>): RouteStop[] {
  const transport = events.filter(
    (e) => e.eventType === 'TRANSPORT' && (e.locationName || e.UNLocationCode)
  );

  const stops: RouteStop[] = [];
  const seen = new Set<string>();

  for (const e of transport) {
    const key = e.UNLocationCode ?? e.locationName ?? '';
    if (!key) continue;
    if (!seen.has(key)) {
      seen.add(key);
      stops.push({
        key,
        name: formatLocation(e.locationName, e.UNLocationCode, locationMap) ?? key,
        code: e.UNLocationCode,
        status: 'future',
        vessel: e.vesselName,
        voyage: e.voyageNumber,
      });
    }
    // Record earliest departure / latest arrival timestamps
    const stop = stops.find((s) => s.key === key)!;
    if (e.transportEventTypeCode === 'DEPA' && e.eventClassifierCode === 'ACT') {
      if (!stop.departedAt || e.eventDateTime > stop.departedAt) stop.departedAt = e.eventDateTime;
    }
    if (e.transportEventTypeCode === 'ARRI' && e.eventClassifierCode === 'ACT') {
      if (!stop.arrivedAt || e.eventDateTime < stop.arrivedAt) stop.arrivedAt = e.eventDateTime;
    }
  }

  // Fall back to equipment events (LOAD/DISC, GTOT/GTIN) when the carrier
  // doesn't upgrade transport ARRI/DEPA to ACT. Hapag-Lloyd, ZIM, and ONE
  // frequently emit ACT equipment events while transport stays EST/PLN —
  // without this, every stop stays `future` and the strip shows no movement.
  // LOAD = container onto vessel → departed. DISC = container off vessel → arrived.
  // GTOT = gate out from origin yard. GTIN = gate in at final yard.
  for (const e of events) {
    if (e.eventType !== 'EQUIPMENT' || e.eventClassifierCode !== 'ACT') continue;
    const key = e.UNLocationCode ?? '';
    if (!key) continue;
    const stop = stops.find((s) => s.key === key);
    if (!stop) continue;
    const code = e.equipmentEventTypeCode;
    if (code === 'LOAD' || code === 'GTOT') {
      if (!stop.departedAt || e.eventDateTime > stop.departedAt) stop.departedAt = e.eventDateTime;
    }
    if (code === 'DISC' || code === 'GTIN') {
      if (!stop.arrivedAt || e.eventDateTime < stop.arrivedAt) stop.arrivedAt = e.eventDateTime;
    }
  }

  // Determine current position = last stop that has an actual departure or arrival
  let lastActIndex = -1;
  for (let i = 0; i < stops.length; i++) {
    if (stops[i].departedAt || stops[i].arrivedAt) lastActIndex = i;
  }

  for (let i = 0; i < stops.length; i++) {
    if (i < lastActIndex) stops[i].status = 'past';
    else if (i === lastActIndex) stops[i].status = 'current';
    else stops[i].status = 'future';
  }

  return stops;
}

function getOrigin(stops: RouteStop[]) {
  return stops[0]?.name ?? null;
}
function getDestination(stops: RouteStop[]) {
  return stops[stops.length - 1]?.name ?? null;
}

function getETA(events: TrackingEvent[]): string | null {
  // Last PLN or EST ARRI event that is still in the future
  const now = Date.now();
  const planned = events
    .filter(
      (e) =>
        e.eventType === 'TRANSPORT' &&
        e.transportEventTypeCode === 'ARRI' &&
        (e.eventClassifierCode === 'PLN' || e.eventClassifierCode === 'EST') &&
        new Date(e.eventDateTime).getTime() > now
    )
    .sort((a, b) => new Date(b.eventDateTime).getTime() - new Date(a.eventDateTime).getTime());
  return planned[0]?.eventDateTime ?? null;
}

function getLatestActualEvent(events: TrackingEvent[]): TrackingEvent | null {
  const now = Date.now();
  const actual = events.filter(
    (e) => e.eventClassifierCode === 'ACT' && new Date(e.eventDateTime).getTime() <= now
  );
  return actual[actual.length - 1] ?? null;
}

function getCarrierName(result: TrackingResult): string {
  if (result.detectedCarrier) return result.detectedCarrier;
  if (result.carrier) return result.carrier;
  // Infer from first event
  return result.events[0]?.carrier ?? result.carriersQueried?.[0] ?? '';
}

interface TrackingWidgetProps {
  initialQuery?: string;
  embedded?: boolean;
  partnerSlug?: string;
}

export function TrackingWidget({ initialQuery, embedded = false, partnerSlug }: TrackingWidgetProps) {
  const [input, setInput] = useState(initialQuery ?? '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TrackingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());

  async function doTrack(query: string) {
    if (!query.trim()) return;
    const attribution = { surface: embedded ? 'widget' as const : 'site' as const, ...(partnerSlug && { partner_slug: partnerSlug }) };
    recordProductEvent('tracking_search_started', attribution);
    setLoading(true);
    setError(null);
    setResult(null);
    setShowAllEvents(false);
    setExpandedEvents(new Set());
    try {
      const res = await fetch('/api/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reference: query.trim(), type: 'auto' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Tracking failed');
        recordProductEvent('tracking_search_failed', { ...attribution, http_status: res.status });
      } else {
        setResult(data);
        recordProductEvent('tracking_result_viewed', {
          ...attribution,
          result_kind: data.unavailable ? 'unavailable' : data.paywall ? 'paywall' : !data.events?.length ? 'empty'
            : data.cached || data.stale ? 'archive' : 'live',
        });
      }
    } catch {
      setError('Network error — please try again');
      recordProductEvent('tracking_search_failed', attribution);
    } finally {
      setLoading(false);
    }
  }

  async function handleTrack(e: React.FormEvent) {
    e.preventDefault();
    await doTrack(input);
  }

  useEffect(() => {
    if (initialQuery?.trim()) {
      doTrack(initialQuery);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally run once on mount

  const locationMap = result ? buildLocationMap(result.events) : {};
  const route = result ? extractRoute(result.events, locationMap) : [];
  const origin = getOrigin(route);
  const destination = getDestination(route);
  const eta = result ? getETA(result.events) : null;
  const latestEvent = result ? getLatestActualEvent(result.events) : null;
  const carrier = result ? getCarrierName(result) : '';
  const containers = result ? extractContainersFromEvents(result.events) : [];
  const containerGroups = groupContainersByType(containers);
  const PREVIEW_COUNT = 8;
  const eventsToShow = result
    ? showAllEvents
      ? result.events
      : result.events.slice(0, PREVIEW_COUNT)
    : [];

  return (
    <div className="tracking-widget">
      {/* Search form */}
      <form className="tracking-form" onSubmit={handleTrack}>
        <input
          className="tracking-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Container number or B/L number"
          disabled={loading}
          autoComplete="off"
          spellCheck={false}
        />
        <button className="tracking-submit" type="submit" disabled={loading || !input.trim()}>
          {loading ? 'Tracking…' : 'Track'}
        </button>
      </form>

      <p className="tracking-hint">
        Hapag-Lloyd · MSC · Maersk · CMA CGM Group · COSCO · Yang Ming · ONE · ZIM · Enter container number or B/L
      </p>

      {error && <div className="tracking-error">{error}</div>}

      {result && (result.paywall || result.unavailable) && (
        <div className="tracking-empty-box">
          {result.message ?? `Tracking for ${result.detectedCarrier ?? 'this carrier'} requires a Pro plan.`}
          {result.upgradeUrl && (
            <>
              {' '}
              <a href={result.upgradeUrl} target="_blank" rel="noopener noreferrer">
                {result.upgradeLabel ?? 'Upgrade'}
              </a>.
            </>
          )}
          {result.events.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.85 }}>
              Showing last known position from cache below.
            </div>
          )}
        </div>
      )}

      {result && !result.paywall && !result.unavailable && result.events.length === 0 && (
        <TrackingEmptyState
          reference={result.reference}
          suggestedCarrier={result.suggestedCarrier}
          suggestedCarrierStage={result.suggestedCarrierStage}
          detectedCarrier={result.detectedCarrier}
        />
      )}

      {result && result.events.length > 0 && (
        <div className="tr-card">

          {/* ── Header ── */}
          <div className="tr-header">
            <div className="tr-header-left">
              <div className="tr-ref-label">
                {result.referenceType === 'container' ? 'Container' : result.referenceType === 'booking' ? 'Booking' : 'Bill of Lading'}
              </div>
              <div className="tr-ref-number">{result.reference}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {(result.paywall || result.cached || result.stale) && (
                <div
                  className="tr-carrier-badge"
                  style={{ background: '#fef3c7', color: '#92400e' }}
                  title="Showing cached events — live tracking is unavailable for this carrier (paywalled, kill-switched, or all carriers errored)."
                >
                  CACHED
                </div>
              )}
              {carrier && <div className="tr-carrier-badge">{carrier.toUpperCase()}</div>}
            </div>
          </div>

          {/* ── Origin → Destination ── */}
          {(origin || destination) && (
            <div className="tr-route-header">
              <div className="tr-port tr-port--origin">
                <div className="tr-port-label">From</div>
                <div className="tr-port-name">{origin ?? '—'}</div>
              </div>
              <div className="tr-route-arrow">→</div>
              <div className="tr-port tr-port--destination">
                <div className="tr-port-label">To</div>
                <div className="tr-port-name">{destination ?? '—'}</div>
              </div>
            </div>
          )}

          {/* ── Latest event + ETA ── */}
          <div className="tr-status-row">
            {latestEvent && (
              <div className="tr-status-item">
                <div className="tr-status-label">Latest event</div>
                <div className="tr-status-value">
                  {eventLabel(latestEvent)}
                  {(latestEvent.locationName || latestEvent.UNLocationCode) && (
                    <span className="tr-status-loc">
                      {' · '}{formatLocation(latestEvent.locationName, latestEvent.UNLocationCode, locationMap)}
                    </span>
                  )}
                </div>
                <div className="tr-status-date">{formatDate(latestEvent.eventDateTime)}</div>
              </div>
            )}
            {eta && (
              <div className="tr-status-item tr-status-item--eta">
                <div className="tr-status-label">Est. arrival</div>
                <div className="tr-status-value tr-status-value--eta">{formatShort(eta)}</div>
              </div>
            )}
          </div>

          {/* ── Container summary ── */}
          {containers.length > 0 && (
            <div className="tr-containers-section">
              <div className="tr-containers-header">
                <span>{containers.length} {containers.length === 1 ? 'Container' : 'Containers'}</span>
                {containerGroups.map((g, i) => (
                  <span key={i} className="tr-containers-type-badge">
                    {g.count} &times; {g.label}
                  </span>
                ))}
              </div>
              <div className="tr-containers-list">
                {containers.map((c) => (
                  <span key={c.reference} className="tr-container-tag">
                    {c.reference}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Route stops ── */}
          {route.length > 1 && (
            <div className="tr-route-strip-wrap">
              <div className="tr-route-strip">
                {route.map((stop, i) => (
                  <div key={stop.key} className="tr-stop-group">
                    <div className={`tr-stop tr-stop--${stop.status}`}>
                      <div className="tr-stop-marker">
                        {stop.status === 'past' && <span className="tr-stop-tick">■</span>}
                        {stop.status === 'current' && <span className="tr-stop-current">■</span>}
                        {stop.status === 'future' && <span className="tr-stop-future">□</span>}
                      </div>
                      <div className="tr-stop-name">{stop.name}</div>
                      {stop.status === 'current' && (
                        <div className="tr-stop-here">HERE</div>
                      )}
                    </div>
                    {i < route.length - 1 && (
                      <div className={`tr-stop-line tr-stop-line--${route[i + 1].status === 'future' ? 'future' : 'past'}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Save Shipment ── */}
          <SaveShipmentButton
            result={result}
            route={route}
            eta={eta}
          />

          {/* ── Destination Agent CTA ── */}
          <DestinationCTA
            destinationCountry={route[route.length - 1]?.code?.slice(0, 2) ?? null}
            blReference={result.reference}
            destinationPort={route[route.length - 1]?.code ?? undefined}
          />

          {/* ── Events timeline ── */}
          {(() => {
            const discEvent = result.events.find(
              (e: TrackingEvent) => e.equipmentEventTypeCode === 'DISC' && e.eventClassifierCode === 'ACT'
            );
            const gtotEvent = result.events.find(
              (e: TrackingEvent) => e.equipmentEventTypeCode === 'GTOT' && e.eventClassifierCode === 'ACT'
            );
            const hasReturn = result.events.some(
              (e: TrackingEvent) => (e.equipmentEventTypeCode === 'GTIN' || e.equipmentEventTypeCode === 'RETN') && e.eventClassifierCode === 'ACT'
            );
            const destCode = route[route.length - 1]?.code ?? null;
            const discLoc = discEvent?.UNLocationCode ?? '';
            const isAtDestination = !destCode || discLoc === destCode;
            const hasSubsequentLoad = discEvent?.eventDateTime ? result.events.some(
              (e: TrackingEvent) =>
                (e.equipmentEventTypeCode === 'LOAD' || e.transportEventTypeCode === 'DEPA') &&
                e.eventDateTime &&
                new Date(e.eventDateTime) > new Date(discEvent.eventDateTime) &&
                (e.UNLocationCode === discLoc || !e.UNLocationCode)
            ) : false;
            const demurrageDays = discEvent?.eventDateTime && !gtotEvent && isAtDestination && !hasSubsequentLoad
              ? Math.floor((Date.now() - new Date(discEvent.eventDateTime).getTime()) / 86_400_000)
              : 0;
            const detentionDays = gtotEvent?.eventDateTime && !hasReturn
              ? Math.floor((Date.now() - new Date(gtotEvent.eventDateTime).getTime()) / 86_400_000)
              : 0;
            const demurragePort = discEvent?.locationName ?? discEvent?.UNLocationCode ?? 'port';

            return (
              <>
                {demurrageDays >= 3 && (
                  <div className="tr-dd-alert tr-dd-alert--demurrage">
                    <div>
                      <div className="tr-dd-alert-label">DEMURRAGE RISK</div>
                      <div className="tr-dd-alert-desc">Container idle at {demurragePort} for {demurrageDays} days since discharge. Free time may be expiring.</div>
                    </div>
                  </div>
                )}
                {detentionDays >= 5 && (
                  <div className="tr-dd-alert tr-dd-alert--detention">
                    <div>
                      <div className="tr-dd-alert-label">DETENTION RISK</div>
                      <div className="tr-dd-alert-desc">Container not returned for {detentionDays} days since pickup. Detention charges may be accruing.</div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          <div className="tr-events-header">
            <span>Events</span>
            <span className="tr-events-count">{result.events.length} total</span>
          </div>
          <ol className="tr-timeline">
            {eventsToShow.map((event, i) => {
              const isExpanded = expandedEvents.has(i);
              const hasDetails = hasEventDetails(event);
              const decodedEquipment = decodeISOEquipment(event.ISOEquipmentCode);

              return (
                <li
                  key={i}
                  className={`tr-event tr-event--${event.eventClassifierCode.toLowerCase()}`}
                >
                  <div className="tr-event-marker" />
                  <div className="tr-event-body">
                    <div className="tr-event-top">
                      <span className="tr-event-label">{eventLabel(event)}</span>
                      <span className="tr-event-classifier">
                        {event.eventClassifierCode === 'ACT' ? 'ACT' : event.eventClassifierCode === 'EST' ? 'EST' : 'PLN'}
                      </span>
                    </div>
                    {event.description && (
                      <div className="tr-event-description">{event.description}</div>
                    )}
                    {(event.locationName || event.UNLocationCode) && (
                      <div className="tr-event-location">
                        {formatLocation(event.locationName, event.UNLocationCode, locationMap)}
                      </div>
                    )}
                    {event.vesselName && (
                      <div className="tr-event-vessel">
                        {event.vesselName}
                        {event.voyageNumber && ` / ${event.voyageNumber}`}
                      </div>
                    )}
                    <div className="tr-event-date">{formatDate(event.eventDateTime)}</div>

                    {hasDetails && (
                      <button
                        className={`tr-event-details-toggle ${isExpanded ? 'tr-event-details-toggle--open' : ''}`}
                        onClick={() => {
                          setExpandedEvents((prev) => {
                            const next = new Set(prev);
                            if (next.has(i)) next.delete(i);
                            else next.add(i);
                            return next;
                          });
                        }}
                      >
                        <span className="tr-event-details-arrow">{isExpanded ? '▾' : '▸'}</span>
                        {isExpanded ? 'Hide details' : 'Details'}
                      </button>
                    )}

                    {isExpanded && (
                      <div className="tr-event-details">
                        {event.equipmentReference && (
                          <div className="tr-detail-row">
                            <span className="tr-detail-label">Container</span>
                            <span className="tr-detail-value tr-detail-mono">{event.equipmentReference}</span>
                          </div>
                        )}
                        {event.ISOEquipmentCode && (
                          <div className="tr-detail-row">
                            <span className="tr-detail-label">Equipment type</span>
                            <span className="tr-detail-value">
                              {decodedEquipment ?? event.ISOEquipmentCode}
                              <span className="tr-detail-code">{event.ISOEquipmentCode}</span>
                            </span>
                          </div>
                        )}
                        {event.emptyIndicatorCode && (
                          <div className="tr-detail-row">
                            <span className="tr-detail-label">Status</span>
                            <span className={`tr-detail-badge ${event.emptyIndicatorCode === 'LADEN' ? 'tr-detail-badge--laden' : 'tr-detail-badge--empty'}`}>
                              {event.emptyIndicatorCode}
                            </span>
                          </div>
                        )}
                        {event.vesselIMONumber && (
                          <div className="tr-detail-row">
                            <span className="tr-detail-label">IMO number</span>
                            <span className="tr-detail-value tr-detail-mono">{event.vesselIMONumber}</span>
                          </div>
                        )}
                        {event.vesselFlag && (
                          <div className="tr-detail-row">
                            <span className="tr-detail-label">Vessel flag</span>
                            <span className="tr-detail-value">{event.vesselFlag}</span>
                          </div>
                        )}
                        {event.vesselCallSign && (
                          <div className="tr-detail-row">
                            <span className="tr-detail-label">Call sign</span>
                            <span className="tr-detail-value tr-detail-mono">{event.vesselCallSign}</span>
                          </div>
                        )}
                        {event.facilityCode && (
                          <div className="tr-detail-row">
                            <span className="tr-detail-label">Terminal code</span>
                            <span className="tr-detail-value tr-detail-mono">{event.facilityCode}</span>
                          </div>
                        )}
                        {event.facilityAddress && (
                          <div className="tr-detail-row">
                            <span className="tr-detail-label">Facility</span>
                            <span className="tr-detail-value">{event.facilityAddress}</span>
                          </div>
                        )}
                        {event.carrierServiceCode && (
                          <div className="tr-detail-row">
                            <span className="tr-detail-label">Service</span>
                            <span className="tr-detail-value tr-detail-mono">{event.carrierServiceCode}</span>
                          </div>
                        )}
                        {event.seals && event.seals.length > 0 && (
                          <div className="tr-detail-row">
                            <span className="tr-detail-label">{event.seals.length === 1 ? 'Seal' : 'Seals'}</span>
                            <span className="tr-detail-value">
                              {event.seals.map((s, si) => (
                                <span key={si} className="tr-detail-seal">
                                  {s.sealNumber}
                                  {s.sealSource && <span className="tr-detail-code">{s.sealSource}</span>}
                                  {s.sealType && <span className="tr-detail-code">{s.sealType}</span>}
                                </span>
                              ))}
                            </span>
                          </div>
                        )}
                        {event.documentReferences && event.documentReferences.length > 0 && (
                          <div className="tr-detail-row tr-detail-row--docs">
                            <span className="tr-detail-label">References</span>
                            <div className="tr-detail-docs">
                              {event.documentReferences.map((d, di) => (
                                <div key={di} className="tr-detail-doc">
                                  <span className="tr-detail-doc-type">{DOC_REF_LABELS[d.documentReferenceType] ?? d.documentReferenceType}</span>
                                  <span className="tr-detail-mono">{d.documentReferenceValue}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          {result.events.length > PREVIEW_COUNT && (
            <button
              className="tr-show-more"
              onClick={() => setShowAllEvents(!showAllEvents)}
            >
              {showAllEvents
                ? 'Show less'
                : `Show all ${result.events.length} events`}
            </button>
          )}

        </div>
      )}
    </div>
  );
}
